import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import QRCode from 'qrcode';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  MinusCircleIcon,
  CameraIcon,
  QrCodeIcon,
  TrashIcon,
  PlusIcon,
  LinkIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import WhatsAppIcon from '../components/WhatsAppIcon';
import { OdometerScanButton } from '../components/OdometerScanner';
import { getAppSocket } from '../lib/wesSocket';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tip } from '../components/ui/tip';
import { VehicleServicePanel } from '../components/VehicleServicePanel';
import { useAuth } from '../contexts/AuthContext';
import { openPdf } from '../utils/pdfHelpers';

type PointState = 'ok' | 'atencion' | 'urgente' | 'na';

interface TemplatePoint { key: string; label: string; notas?: string[]; notasGrupos?: (string | null)[] }
interface TemplateSection { key: string; label: string; points: TemplatePoint[]; areas?: string[] }

interface PointData { state: PointState | null; note: string; images: string[] }

// Saca de la nota los mensajes predefinidos: lo que queda es el texto a mano
const quitarPresets = (note: string, notas: string[]) => {
  let s = note || '';
  for (const n of notas) s = s.split(n).join(' ');
  return s.replace(/\s+/g, ' ').trim();
};
interface FreeItem { description: string; state: PointState; images: string[] }

// Lo que ya sabíamos de este punto por una visita anterior. Se muestra en gris
// como referencia: NO se guarda salvo que el operario toque el punto hoy, así
// la hoja nunca afirma que se revisó algo que no se revisó.
interface PuntoPrevio {
  state: PointState;
  checkedAt?: string | null;
  checkedKm?: number | null;
  note?: string | null;
  situacion?: string;
}

const fechaCorta = (ymd?: string | null) => {
  if (!ymd) return null;
  const [y, m, d] = String(ymd).slice(0, 10).split('-');
  // Con el año: un punto heredado puede venir de hace años y "8/8" solo no dice nada
  return y && m && d ? `${Number(d)}/${Number(m)}/${y.slice(2)}` : null;
};

interface DiagnosisHeader {
  id: string;
  vehicleId?: string | null;
  diagnosisNumber: string;
  clientName?: string;
  vehiclePlate?: string;
  vehicleBrand?: string;
  vehicleModel?: string;
  mileage?: number | null;
  observations?: string | null;
  status: string;
  relatedBudgetId?: string | null;
  performedBy?: string | null;
}

const STATE_META: Record<PointState, { label: string; ring: string; solid: string; text: string }> = {
  ok:       { label: 'Bien',     ring: 'border-emerald-500/60 text-emerald-500', solid: 'bg-emerald-500 text-white border-emerald-500', text: 'text-emerald-500' },
  atencion: { label: 'Atención', ring: 'border-amber-500/60 text-amber-500',     solid: 'bg-amber-500 text-white border-amber-500',     text: 'text-amber-500' },
  urgente:  { label: 'Urgente',  ring: 'border-red-500/60 text-red-500',         solid: 'bg-red-500 text-white border-red-500',         text: 'text-red-500' },
  na:       { label: 'No aplica', ring: 'border-muted-foreground/40 text-muted-foreground', solid: 'bg-muted-foreground text-white border-muted-foreground', text: 'text-muted-foreground' }
};

const StateIcon = ({ state, className }: { state: PointState; className?: string }) => {
  if (state === 'ok') return <CheckCircleIcon className={className} />;
  if (state === 'atencion') return <ExclamationTriangleIcon className={className} />;
  if (state === 'na') return <MinusCircleIcon className={className} />;
  return <XCircleIcon className={className} />;
};

export default function DiagnosticChecklist() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  // Solo el admin elige quién revisó; un operario queda registrado él mismo
  const isAdmin = (user as any)?.role === 'admin' || hasPermission('manage_settings');

  const [sections, setSections] = useState<TemplateSection[]>([]);
  // Puntos donde se eligió "Otro (escribir a mano)" en el select de mensajes
  const [manualNote, setManualNote] = useState<Record<string, boolean>>({});
  // Texto a mano en curso por punto: mientras se tipea manda este estado; la
  // nota combinada (presets + manual) se normaliza recién al armarse. Si se
  // derivara de la nota en cada tecla, el trim se comería los espacios.
  const [manualText, setManualText] = useState<Record<string, string>>({});
  const [diagnosis, setDiagnosis] = useState<DiagnosisHeader | null>(null);
  const [points, setPoints] = useState<Record<string, PointData>>({});
  // Estado heredado de visitas anteriores, por pointKey
  const [previo, setPrevio] = useState<Record<string, PuntoPrevio>>({});
  const [freeItems, setFreeItems] = useState<FreeItem[]>([]);
  const [mileage, setMileage] = useState<string>('');
  const [observations, setObservations] = useState<string>('');
  // Lo que reporta el dueño del auto al traerlo (opcional, referencia para el que revisa)
  const [customerComplaint, setCustomerComplaint] = useState<string>('');
  // Quién realizó la inspección (usuario del sistema)
  const [performedBy, setPerformedBy] = useState<string>('');
  const [users, setUsers] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);

  // El checklist es transversal: todos lo ven entero, pero cada operario
  // arranca con SUS secciones expandidas (según su área) y las ajenas
  // colapsadas. El admin (o quien no tiene área) ve todo expandido.
  const miArea = useMemo(() => {
    if (isAdmin) return null;
    return String((user as any)?.areaName || '')
      .normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim() || null;
  }, [user, isAdmin]);
  const esMia = (sec: TemplateSection) =>
    !miArea || !sec.areas || sec.areas.length === 0 || sec.areas.includes(miArea);
  const [colapsadas, setColapsadas] = useState<Record<string, boolean>>({});

  // Fotos: destino actual ('point:<key>' | 'free:<idx>') para cámara y QR
  const [photoTarget, setPhotoTarget] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [qr, setQr] = useState<{ token: string; dataUrl: string; received: number } | null>(null);
  const qrTargetRef = useRef<string | null>(null);
  const qrTokenRef = useRef<string | null>(null);
  const qrCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Con el QR cerrado, la sesion sigue aceptando fotos en camino por un rato
  // corto; pasado ese tiempo sin actividad se termina (aca y en el server)
  const qrGraceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const QR_GRACE_MS = 2 * 60 * 1000;

  const endQrSession = () => {
    if (qrGraceTimerRef.current) { clearTimeout(qrGraceTimerRef.current); qrGraceTimerRef.current = null; }
    const token = qrTokenRef.current;
    qrTokenRef.current = null;
    qrTargetRef.current = null;
    if (token) axios.delete(`/api/diagnostics/photo-session/${token}`).catch(() => {});
  };

  const armQrGrace = () => {
    if (qrGraceTimerRef.current) clearTimeout(qrGraceTimerRef.current);
    qrGraceTimerRef.current = setTimeout(endQrSession, QR_GRACE_MS);
  };

  const totalPoints = useMemo(() => sections.reduce((s, sec) => s + sec.points.length, 0), [sections]);
  const counts = useMemo(() => {
    const vals = Object.values(points).filter(p => p.state);
    const freeVals = freeItems.filter(f => f.description.trim());
    return {
      revisados: vals.length,
      atencion: vals.filter(p => p.state === 'atencion').length + freeVals.filter(f => f.state === 'atencion').length,
      urgente: vals.filter(p => p.state === 'urgente').length + freeVals.filter(f => f.state === 'urgente').length
    };
  }, [points, freeItems]);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [tplRes, diagRes] = await Promise.all([
        axios.get('/api/diagnostics/checklist-template'),
        axios.get(`/api/diagnostics/${id}`)
      ]);
      const tplSections: TemplateSection[] = tplRes.data.sections || [];
      setSections(tplSections);
      // Arranque: las secciones de OTRA área nacen colapsadas (se pueden abrir)
      if (miArea) {
        const ini: Record<string, boolean> = {};
        tplSections.forEach(sec => {
          const propia = !sec.areas || sec.areas.length === 0 || sec.areas.includes(miArea);
          if (!propia) ini[sec.key] = true;
        });
        setColapsadas(ini);
      }
      const d = diagRes.data.diagnosis;
      setDiagnosis({ ...d });
      setMileage(d.mileage != null ? String(d.mileage) : '');
      setObservations(d.observations || '');
      setCustomerComplaint(d.customerComplaint || '');
      setPerformedBy(d.performedBy || '');

      // Mapear items guardados → estado del checklist
      const labelByKey: Record<string, string> = {};
      tplSections.forEach(sec => sec.points.forEach(p => { labelByKey[p.key] = p.label; }));
      const pts: Record<string, PointData> = {};
      const free: FreeItem[] = [];
      (diagRes.data.items || []).forEach((it: any) => {
        if (it.pointKey && labelByKey[it.pointKey]) {
          pts[it.pointKey] = {
            state: (it.state as PointState) || null,
            note: it.description && it.description !== labelByKey[it.pointKey] ? it.description : '',
            images: Array.isArray(it.images) ? it.images : []
          };
        } else {
          free.push({
            description: it.description || '',
            state: (it.state as PointState) || (['alta', 'critica'].includes(it.severity) ? 'urgente' : 'atencion'),
            images: Array.isArray(it.images) ? it.images : []
          });
        }
      });
      setPoints(pts);
      setFreeItems(free);
      setDirty(false);

      // Estado vivo del vehículo: lo que ya se sabe de cada punto por visitas
      // anteriores. Se excluye lo revisado en ESTE diagnóstico (eso ya está
      // arriba y no es herencia).
      if (d.vehicleId) {
        try {
          const svc = await axios.get(`/api/service-status/vehicle/${d.vehicleId}`);
          const heredado: Record<string, PuntoPrevio> = {};
          (svc.data.puntos || []).forEach((p: any) => {
            if (!p.state || pts[p.pointKey]) return;
            // Nunca heredar de uno mismo: el checklist se guarda borrando y
            // recreando items, así que un punto puede haber quedado en el
            // estado del vehículo sin estar ya en este diagnóstico.
            if (p.diagnosisId && p.diagnosisId === id) return;
            heredado[p.pointKey] = {
              state: p.state as PointState,
              checkedAt: p.checkedAt,
              checkedKm: p.checkedKm,
              note: p.note,
              situacion: p.situacion
            };
          });
          setPrevio(heredado);
        } catch { setPrevio({}); }
      } else {
        setPrevio({});
      }
    } catch (e) {
      toast.error('No se pudo cargar el diagnóstico');
      navigate('/diagnostics');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  // Usuarios para el "Revisado por" (solo el admin puede elegir).
  // Solo roles que trabajan sobre el vehículo: operarios y admin (afuera finanzas/cm).
  useEffect(() => {
    if (!isAdmin) return;
    axios.get('/api/users')
      .then(res => {
        const REVIEWER_ROLES = ['operario', 'admin'];
        const list = (res.data.users || res.data || [])
          .filter((u: any) => (u.isActive !== false && u.isActive !== 0 && u.active !== false && u.active !== 0))
          .filter((u: any) => REVIEWER_ROLES.includes(String(u.role || '').toLowerCase()))
          .map((u: any) => ({ id: u.id, name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username }));
        setUsers(list);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // Fotos que llegan del celular por QR. La recepción va por refs (token e
  // ítem destino), NO por el estado del dialog: cerrar el QR es solo visual y
  // las fotos que siguen en camino (subida + compresión pueden tardar más que
  // el auto-cierre) se acreditan igual mientras la sesión viva (30 min).
  useEffect(() => {
    const socket = getAppSocket();
    if (!socket) return;
    const handlePhoto = (data: { token: string; path: string }) => {
      if (!qrTokenRef.current || data.token !== qrTokenRef.current) return;
      const target = qrTargetRef.current;
      if (!target) return;
      addImageToTarget(target, data.path);
      setQr(prev => (prev ? { ...prev, received: prev.received + 1 } : prev));
      toast.success('Foto recibida del celular');
      // Cerrar el QR solo, 2s después de la ÚLTIMA foto (si siguen llegando,
      // el timer se resetea — permite mandar varias seguidas)
      if (qrCloseTimerRef.current) clearTimeout(qrCloseTimerRef.current);
      qrCloseTimerRef.current = setTimeout(() => setQr(null), 2000);
      // Cada foto renueva la ventana de gracia
      armQrGrace();
    };
    socket.on('diagnostics:photo', handlePhoto);
    return () => { socket.off('diagnostics:photo', handlePhoto); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Al salir de la pantalla, terminar la sesion de fotos si quedo viva
  useEffect(() => () => endQrSession(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const addImageToTarget = (target: string, path: string) => {
    setDirty(true);
    if (target.startsWith('point:')) {
      const key = target.slice(6);
      setPoints(prev => ({ ...prev, [key]: { ...(prev[key] || { state: null, note: '', images: [] }), images: [...(prev[key]?.images || []), path] } }));
    } else if (target.startsWith('free:')) {
      const idx = Number(target.slice(5));
      setFreeItems(prev => prev.map((f, i) => (i === idx ? { ...f, images: [...f.images, path] } : f)));
    }
  };

  const removeImageFromTarget = (target: string, path: string) => {
    setDirty(true);
    if (target.startsWith('point:')) {
      const key = target.slice(6);
      setPoints(prev => ({ ...prev, [key]: { ...(prev[key] || { state: null, note: '', images: [] }), images: (prev[key]?.images || []).filter(p => p !== path) } }));
    } else if (target.startsWith('free:')) {
      const idx = Number(target.slice(5));
      setFreeItems(prev => prev.map((f, i) => (i === idx ? { ...f, images: f.images.filter(p => p !== path) } : f)));
    }
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !photoTarget) return;
    const fd = new FormData();
    fd.append('image', file);
    try {
      const res = await axios.post('/api/diagnostics/upload-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      addImageToTarget(photoTarget, res.data.image.path);
    } catch {
      toast.error('No se pudo subir la foto');
    }
  };

  const openCamera = (target: string) => { setPhotoTarget(target); setTimeout(() => fileInputRef.current?.click(), 0); };

  const openQr = async (target: string) => {
    try {
      endQrSession(); // si habia otra sesion abierta, terminarla
      qrTargetRef.current = target;
      const res = await axios.post('/api/diagnostics/photo-session');
      qrTokenRef.current = res.data.token;
      const dataUrl = await QRCode.toDataURL(res.data.url, { width: 260, margin: 1 });
      setQr({ token: res.data.token, dataUrl, received: 0 });
    } catch {
      toast.error('No se pudo crear la sesión de fotos');
    }
  };

  const setPointState = (key: string, state: PointState) => {
    setDirty(true);
    const nuevo = points[key]?.state === state ? null : state;
    // El detalle solo existe para "atención" y "urgente": el editor se colapsa
    // al pasar a bien/no aplica, así que la nota quedaba escondida y salía
    // igual en el PDF ("Bien" con el texto de la cubierta gastada al lado).
    const sinDetalle = nuevo === 'ok' || nuevo === 'na' || nuevo === null;
    if (sinDetalle) {
      setManualText(prev => ({ ...prev, [key]: '' }));
      setManualNote(prev => ({ ...prev, [key]: false }));
    }
    setPoints(prev => {
      const cur = prev[key] || { state: null, note: '', images: [] };
      return { ...prev, [key]: { ...cur, state: nuevo, note: sinDetalle ? '' : cur.note } };
    });
  };

  const setSectionOk = (sec: TemplateSection) => {
    setDirty(true);
    setPoints(prev => {
      const next = { ...prev };
      sec.points.forEach(p => {
        const cur = next[p.key];
        if (!cur?.state) next[p.key] = { state: 'ok', note: cur?.note || '', images: cur?.images || [] };
      });
      return next;
    });
  };

  // Versión de los datos: si el usuario toca algo mientras un guardado está en
  // vuelo, el guardado viejo no debe marcar "todo guardado" (autosave)
  const versionRef = useRef(0);
  useEffect(() => {
    if (!loading) versionRef.current++;
  }, [points, freeItems, mileage, observations, customerComplaint, performedBy, loading]);

  const save = async (silent = false): Promise<boolean> => {
    if (!id) return false;
    const version = versionRef.current;
    setSaving(true);
    try {
      const payloadPoints = Object.entries(points)
        .filter(([, p]) => p.state)
        .map(([pointKey, p]) => ({ pointKey, state: p.state, note: p.note, images: p.images }));
      const payloadFree = freeItems
        .filter(f => f.description.trim())
        .map(f => ({ description: f.description, state: f.state, images: f.images }));
      await axios.put(`/api/diagnostics/${id}/checklist`, {
        points: payloadPoints,
        freeItems: payloadFree,
        mileage: mileage !== '' ? Number(mileage) : null,
        observations,
        customerComplaint,
        // Operario: manda su propio usuario (el backend lo fuerza igual)
        performedBy: isAdmin ? (performedBy || null) : (user as any)?.id || null
      });
      // Solo marcar limpio si no hubo cambios nuevos durante el request
      if (versionRef.current === version) setDirty(false);
      if (!silent) toast.success('Checklist guardado');
      return true;
    } catch {
      toast.error('No se pudo guardar el checklist');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Autosave: cualquier cambio se guarda solo tras una pausa corta (sin apretar
  // Guardar). Si hay un guardado en vuelo, se reintenta al terminar (saving es dep).
  useEffect(() => {
    if (loading || !dirty || saving) return;
    const t = setTimeout(() => { void save(true); }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, saving, loading, points, freeItems, mileage, observations, customerComplaint, performedBy]);

  // Acciones (guardan antes de ejecutar)
  const withSave = (name: string, fn: () => Promise<void>) => async () => {
    if (busyAction) return;
    setBusyAction(name);
    try {
      const ok = await save(true);
      if (ok) await fn();
    } finally {
      setBusyAction(null);
    }
  };

  const copyLink = withSave('link', async () => {
    const res = await axios.post(`/api/diagnostics/${id}/share-link`);
    await navigator.clipboard.writeText(res.data.url);
    toast.success('Link copiado — pegalo en el chat del cliente');
  });

  const sendWhatsApp = withSave('wpp', async () => {
    try {
      await axios.post(`/api/diagnostics/${id}/send-whatsapp`);
      toast.success('Informe enviado por WhatsApp');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'No se pudo enviar por WhatsApp');
    }
  });

  // Mismo visor que el resto del sistema: en el celular abre la hoja con "Imprimir en
  // el local" y "Descargar" (antes abría una pestaña después de esperar el guardado y
  // el iPhone la bloqueaba: el botón no hacía nada).
  const verPdf = withSave('pdf', async () => {
    try {
      const res = await axios.post(`/api/diagnostics/${id}/pdf-token`);
      const pdf = await axios.get(res.data.url, { responseType: 'blob' });
      openPdf({ blob: pdf.data, filename: `Diagnostico-${diagnosis?.diagnosisNumber || id}.pdf` });
    } catch {
      toast.error('No se pudo generar el PDF');
    }
  });

  const toBudget = withSave('budget', async () => {
    try {
      await axios.post(`/api/diagnostics/${id}/to-budget`);
      toast.success('Presupuesto creado con los ítems en atención/urgentes. Cargale los precios.');
      navigate('/budgets');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'No se pudo generar el presupuesto');
    }
  });

  if (loading || !diagnosis) {
    return <div className="p-6 text-sm text-muted-foreground">Cargando checklist...</div>;
  }

  const vehicleLabel = [diagnosis.vehiclePlate, [diagnosis.vehicleBrand, diagnosis.vehicleModel].filter(Boolean).join(' ')]
    .filter(Boolean).join(' · ');

  const renderPhotoRow = (target: string, images: string[]) => (
    <div className="flex flex-wrap items-center gap-2">
      <Button type="button" variant="outline" size="sm" className="h-9" onClick={() => openCamera(target)}>
        <CameraIcon className="h-4 w-4 mr-1" /> Foto
      </Button>
      {/* El QR es para mandar fotos desde el celu cuando se carga en la compu; en el celu no aporta */}
      <Button type="button" variant="outline" size="sm" className="hidden md:inline-flex h-8" onClick={() => openQr(target)}>
        <QrCodeIcon className="h-4 w-4 mr-1" /> Desde el celu
      </Button>
      {images.map(img => (
        <div key={img} className="relative group">
          <img src={img} alt="foto" className="h-12 w-12 rounded-md object-cover border" />
          <button
            type="button"
            onClick={() => removeImageFromTarget(target, img)}
            className="absolute -top-1.5 -right-1.5 flex md:hidden md:group-hover:flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white"
          >
            <XCircleIcon className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );

  return (
    <div className="mx-auto w-full max-w-3xl px-3 pb-28 pt-3 md:px-6 md:pt-6">
      {/* input oculto para cámara/galería: sin `capture` el teléfono ofrece
          sacar foto O elegir de la galería en el mismo botón */}
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelected} />

      {/* Header */}
      <div className="flex items-start gap-2">
        <Button variant="outline" size="icon" className="shrink-0" onClick={() => navigate('/diagnostics')}>
          <ArrowLeftIcon className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-lg font-bold md:text-2xl">Checklist {diagnosis.diagnosisNumber}</h1>
          <p className="truncate text-sm text-muted-foreground">
            {diagnosis.clientName || 'Sin cliente'}{vehicleLabel ? ` — ${vehicleLabel}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <Input
            inputMode="numeric"
            placeholder="Km"
            value={mileage}
            onChange={e => { setMileage(e.target.value.replace(/\D/g, '')); setDirty(true); }}
            className="h-9 w-24 text-right"
          />
          <OdometerScanButton
            onKm={(km) => { setMileage(String(km)); setDirty(true); }}
            kmReferencia={Number((diagnosis as any).vehicleMileage) || undefined}
            className="h-9 shrink-0"
          />
        </div>
      </div>
      {/* Aviso: km menor al registrado en la ficha (no bloquea: puede ser un
          cambio de tablero o un documento cargado tarde, pero suele ser typo) */}
      {mileage !== '' && Number((diagnosis as any).vehicleMileage) > 0 && Number(mileage) < Number((diagnosis as any).vehicleMileage) && (
        <p className="mt-1.5 text-xs font-medium text-amber-600 dark:text-amber-400">
          Ojo: la ficha del auto tiene {Number((diagnosis as any).vehicleMileage).toLocaleString('es-AR')} km registrados y estás cargando menos. Si es un error de tipeo, corregilo — la ficha no retrocede.
        </p>
      )}

      {/* Quién hizo la inspección: el admin elige; el operario queda registrado él mismo */}
      <div className="mt-2 flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Revisado por</span>
        {isAdmin ? (
          <Select value={performedBy || 'none'} onValueChange={v => { setPerformedBy(v === 'none' ? '' : v); setDirty(true); }}>
            <SelectTrigger className="h-9 w-[200px]">
              <SelectValue placeholder="Elegir..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Sin asignar</SelectItem>
              {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : (
          <span className="text-sm font-medium">
            {`${(user as any)?.firstName || ''} ${(user as any)?.lastName || ''}`.trim() || (user as any)?.username || 'Vos'}
          </span>
        )}
      </div>

      {/* Lo que este auto ya venía debiendo, para tenerlo a mano al revisarlo */}
      {diagnosis.vehicleId && (
        <div className="mt-3">
          <VehicleServicePanel
            vehicleId={diagnosis.vehicleId}
            compacto
            titulo="Ya venía pendiente"
            excluirDiagnosisId={id}
          />
        </div>
      )}

      {/* Secciones */}
      <div className="mt-4 space-y-4">
        {/* Lo que dice el cliente: referencia de por qué trajo el auto (opcional) */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Lo que dice el cliente</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <Textarea
              value={customerComplaint}
              onChange={e => { setCustomerComplaint(e.target.value); setDirty(true); }}
              placeholder="Ej: siente un ruido en el tren delantero al frenar (opcional)"
              className="min-h-[56px] text-sm"
            />
          </CardContent>
        </Card>

        {sections.map(sec => {
          const propia = esMia(sec);
          const cerrada = !!colapsadas[sec.key];
          const revisados = sec.points.filter(p => points[p.key]?.state).length;
          return (
          <Card key={sec.key} className={!propia ? 'border-dashed' : ''}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <button
                type="button"
                className="flex min-w-0 items-center gap-2 text-left"
                onClick={() => setColapsadas(prev => ({ ...prev, [sec.key]: !cerrada }))}
              >
                <CardTitle className="text-base">{sec.label}</CardTitle>
                {miArea && !propia && (
                  <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">otra área</span>
                )}
                <span className={`shrink-0 text-xs ${revisados === sec.points.length ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}>
                  {revisados}/{sec.points.length}
                </span>
                {cerrada && <ChevronDownIcon className="h-4 w-4 shrink-0 text-muted-foreground" />}
              </button>
              {!cerrada && (
                <Button type="button" variant="ghost" size="sm" className="h-7 text-xs text-emerald-600 dark:text-emerald-400" onClick={() => setSectionOk(sec)}>
                  <CheckCircleIcon className="mr-1 h-4 w-4" /> Todo OK
                </Button>
              )}
            </CardHeader>
            {!cerrada && (
            <CardContent className="space-y-1 pt-0">
              {sec.points.map(p => {
                const data = points[p.key] || { state: null, note: '', images: [] };
                const expanded = data.state === 'atencion' || data.state === 'urgente';
                // Sin tocar hoy pero con historia: se muestra lo de antes con SU
                // fecha, no la de hoy. Si no lo tocás, no se guarda nada.
                const prev = !data.state ? previo[p.key] : null;
                return (
                  <div key={p.key} className="rounded-lg border border-transparent px-1 py-1.5 hover:border-border">
                    <div className="flex items-center gap-2">
                      <span className={`min-w-0 flex-1 truncate text-sm ${data.state ? '' : 'text-muted-foreground'}`}>{p.label}</span>
                      {prev && (
                        <Tip label={`Del ${fechaCorta(prev.checkedAt) || 'diagnóstico anterior'}${prev.checkedKm ? ` a los ${prev.checkedKm.toLocaleString('es-AR')} km` : ''}. Tocá un estado solo si lo revisaste hoy.`}>
                          <span className={`shrink-0 whitespace-nowrap rounded-md border border-dashed border-current/40 px-1.5 py-0.5 text-[11px] opacity-70 ${STATE_META[prev.state].text}`}>
                            {STATE_META[prev.state].label} · {fechaCorta(prev.checkedAt)}
                          </span>
                        </Tip>
                      )}
                      <div className="flex shrink-0 items-center gap-1.5">
                        {(['ok', 'atencion', 'urgente', 'na'] as PointState[]).map(st => (
                          <Tip key={st} label={STATE_META[st].label}>
                            <button
                              type="button"
                              onClick={() => setPointState(p.key, st)}
                              className={`flex h-9 w-9 items-center justify-center rounded-full border-2 transition-colors md:h-8 md:w-8 ${
                                data.state === st ? STATE_META[st].solid : `bg-transparent ${STATE_META[st].ring} opacity-60 hover:opacity-100`
                              }`}
                            >
                              <StateIcon state={st} className="h-5 w-5" />
                            </button>
                          </Tip>
                        ))}
                      </div>
                    </div>
                    {expanded && (() => {
                      const notas = p.notas || [];
                      const tienePresets = notas.length > 0;
                      // La nota final es la suma de los mensajes activados + el texto a
                      // mano. Los activos se detectan por presencia en la nota, así se
                      // reconstruyen solos al reabrir un diagnóstico guardado.
                      const activos = notas.filter(n => data.note.includes(n));
                      const textoManual = manualText[p.key] !== undefined ? manualText[p.key] : quitarPresets(data.note, notas);
                      const escribeAMano = !tienePresets || manualNote[p.key] || !!textoManual;
                      const armarNota = (activosNuevos: string[], manual: string) =>
                        [...notas.filter(n => activosNuevos.includes(n)), manual.trim()].filter(Boolean).join(' ');
                      return (
                      <div className="mt-2 space-y-2 rounded-lg bg-muted/40 p-2">
                        {tienePresets && (
                          <div className="space-y-1">
                            {notas.map((n) => {
                              const activa = activos.includes(n);
                              return (
                                <button
                                  key={n}
                                  type="button"
                                  onClick={() => {
                                    setDirty(true);
                                    // Mensajes del mismo grupo son excluyentes (ej: dibujo al
                                    // límite vs desgaste avanzado): activar uno apaga el otro
                                    const grupos = p.notasGrupos || [];
                                    const grupo = grupos[notas.indexOf(n)] ?? null;
                                    const nuevos = activa
                                      ? activos.filter(x => x !== n)
                                      : [...activos.filter(x => grupo == null || (grupos[notas.indexOf(x)] ?? null) !== grupo), n];
                                    setPoints(prev => ({ ...prev, [p.key]: { ...data, note: armarNota(nuevos, textoManual) } }));
                                  }}
                                  className={`flex w-full items-start gap-2 rounded-md border px-2 py-1.5 text-left text-sm transition-colors ${
                                    activa ? 'border-primary bg-primary/10' : 'border-border bg-background hover:bg-muted'
                                  }`}
                                >
                                  <CheckCircleIcon className={`mt-0.5 h-4 w-4 shrink-0 ${activa ? 'text-primary' : 'text-muted-foreground/40'}`} />
                                  <span>{n}</span>
                                </button>
                              );
                            })}
                            {!escribeAMano && (
                              <button
                                type="button"
                                onClick={() => setManualNote(prev => ({ ...prev, [p.key]: true }))}
                                className="flex w-full items-start gap-2 rounded-md border border-dashed border-border bg-background px-2 py-1.5 text-left text-sm text-muted-foreground hover:bg-muted"
                              >
                                <PlusIcon className="mt-0.5 h-4 w-4 shrink-0" />
                                <span>Otro (escribir a mano)</span>
                              </button>
                            )}
                          </div>
                        )}
                        {escribeAMano && (
                          <Textarea
                            value={tienePresets ? textoManual : data.note}
                            onChange={e => {
                              setDirty(true);
                              if (tienePresets) {
                                setManualText(prev => ({ ...prev, [p.key]: e.target.value }));
                                // la caja no se cierra aunque se borre todo el texto
                                setManualNote(prev => (prev[p.key] ? prev : { ...prev, [p.key]: true }));
                                setPoints(prev => ({ ...prev, [p.key]: { ...data, note: armarNota(activos, e.target.value) } }));
                              } else {
                                setPoints(prev => ({ ...prev, [p.key]: { ...data, note: e.target.value } }));
                              }
                            }}
                            placeholder={tienePresets ? 'Texto adicional para el cliente...' : 'Detalle para el cliente (ej: pastillas al 20%, se recomienda cambio)'}
                            className="min-h-[56px] text-sm"
                            autoFocus={tienePresets && !!manualNote[p.key]}
                          />
                        )}
                        {renderPhotoRow(`point:${p.key}`, data.images)}
                      </div>
                      );
                    })()}
                  </div>
                );
              })}
            </CardContent>
            )}
          </Card>
          );
        })}

        {/* Otros hallazgos */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-base">Otros hallazgos</CardTitle>
            <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setDirty(true); setFreeItems(prev => [...prev, { description: '', state: 'atencion', images: [] }]); }}>
              <PlusIcon className="mr-1 h-4 w-4" /> Agregar
            </Button>
          </CardHeader>
          <CardContent className="space-y-3 pt-0">
            {freeItems.length === 0 && (
              <p className="text-sm text-muted-foreground">Algo que no está en el checklist (ej: óptica rota, ruido en tren delantero).</p>
            )}
            {freeItems.map((f, idx) => (
              <div key={idx} className="space-y-2 rounded-lg border p-2">
                <div className="flex items-center gap-2">
                  <Input
                    value={f.description}
                    onChange={e => { setDirty(true); setFreeItems(prev => prev.map((x, i) => (i === idx ? { ...x, description: e.target.value } : x))); }}
                    placeholder="Descripción del hallazgo"
                    className="h-9 flex-1"
                  />
                  <div className="flex shrink-0 items-center gap-1.5">
                    {(['atencion', 'urgente'] as PointState[]).map(st => (
                      <Tip key={st} label={STATE_META[st].label}>
                        <button
                          type="button"
                          onClick={() => { setDirty(true); setFreeItems(prev => prev.map((x, i) => (i === idx ? { ...x, state: st } : x))); }}
                          className={`flex h-9 w-9 items-center justify-center rounded-full border-2 transition-colors ${
                            f.state === st ? STATE_META[st].solid : `bg-transparent ${STATE_META[st].ring} opacity-60 hover:opacity-100`
                          }`}
                        >
                          <StateIcon state={st} className="h-5 w-5" />
                        </button>
                      </Tip>
                    ))}
                    <Tip label="Quitar">
                      <button
                        type="button"
                        onClick={() => { setDirty(true); setFreeItems(prev => prev.filter((_, i) => i !== idx)); }}
                        className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-border text-muted-foreground hover:text-red-500 hover:border-red-500/60"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </Tip>
                  </div>
                </div>
                {renderPhotoRow(`free:${idx}`, f.images)}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Observaciones */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Observaciones generales</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <Textarea
              value={observations}
              onChange={e => { setObservations(e.target.value); setDirty(true); }}
              placeholder="Comentario general para el cliente (opcional)"
              className="min-h-[64px] text-sm"
            />
          </CardContent>
        </Card>

        {/* Acciones de envío */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Enviar al cliente</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-2 pt-0">
            <Button variant="outline" onClick={copyLink} disabled={!!busyAction}>
              <LinkIcon className="mr-1.5 h-4 w-4" /> {busyAction === 'link' ? 'Generando...' : 'Copiar link'}
            </Button>
            <Button variant="outline" onClick={sendWhatsApp} disabled={!!busyAction}>
              <WhatsAppIcon className="mr-1.5 h-4 w-4" /> {busyAction === 'wpp' ? 'Enviando...' : 'WhatsApp'}
            </Button>
            <Button variant="outline" onClick={verPdf} disabled={!!busyAction}>
              <DocumentTextIcon className="mr-1.5 h-4 w-4" /> {busyAction === 'pdf' ? 'Generando...' : 'PDF'}
            </Button>
            {hasPermission('reports') && (
              <Button
                onClick={toBudget}
                disabled={!!busyAction || !!diagnosis.relatedBudgetId || (counts.atencion + counts.urgente) === 0}
              >
                <CurrencyDollarIcon className="mr-1.5 h-4 w-4" />
                {diagnosis.relatedBudgetId ? 'Ya tiene presupuesto' : busyAction === 'budget' ? 'Generando...' : 'Generar presupuesto'}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Barra inferior fija */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="mx-auto flex w-full max-w-3xl items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-2 text-sm">
            <Badge variant="outline">{counts.revisados}/{totalPoints} revisados</Badge>
            {counts.atencion > 0 && <Badge className="bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30" variant="outline">{counts.atencion} atención</Badge>}
            {counts.urgente > 0 && <Badge className="bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30" variant="outline">{counts.urgente} urgente</Badge>}
          </div>
          <Button onClick={() => save()} disabled={saving || !dirty} className="min-w-[110px]">
            {saving ? 'Guardando...' : dirty ? 'Guardar' : 'Guardado'}
          </Button>
        </div>
      </div>

      {/* Dialog QR */}
      {/* Cerrar el dialog no corta la recepción en seco: las fotos en camino
          siguen entrando durante la ventana de gracia y después se termina */}
      <Dialog open={!!qr} onOpenChange={o => { if (!o) { setQr(null); armQrGrace(); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Fotos desde el celular</DialogTitle>
            <DialogDescription>Escaneá el QR con el celular y sacá las fotos; van a aparecer acá solas.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-2">
            {qr && <img src={qr.dataUrl} alt="QR" className="h-64 w-64 rounded-lg bg-white p-2" />}
            {qr && qr.received > 0 && <Badge variant="secondary">{qr.received} foto{qr.received > 1 ? 's' : ''} recibida{qr.received > 1 ? 's' : ''}</Badge>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
